import React, { FC, useMemo, useRef, useState, useEffect } from 'react';
import { addDays, format, isBefore, isSameDay, subDays, startOfDay } from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useAppointmentAvailabilities } from '@utils/api';
import { AppointmentSlot } from '@utils/api/aboboApiTypes';
import AppointmentSlots from '../BookingWizard/AppointmentSlots';
import { useTranslation } from 'react-i18next';
import dateFnsLocales from '@utils/dateFnsLocales';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

type AppointmentPickerProps = {
  serviceId: string;
  operatorIds?: string[];
  startDate?: string;
  endDate?: string;
  onChange?: (slot: AppointmentSlot) => void;
};

const AppointmentPicker: FC<AppointmentPickerProps> = ({ serviceId, onChange, operatorIds, startDate, endDate }) => {
  const [daysToShow, setDaysToShow] = useState(7); // Default to 7 days
  const [currentDate, setCurrentDate] = useState(() => {
    const tomorrow = addDays(new Date(), 1);
    if (startDate) {
      const start = new Date(startDate);
      return isBefore(tomorrow, start) ? start : tomorrow;
    }
    return tomorrow;
  });
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const firstEnabledDateRef = useRef<HTMLButtonElement | null>(null);
  const { t, i18n } = useTranslation();

  // Handle responsive days to show - similar to Chakra's useBreakpointValue
  useEffect(() => {
    const handleResize = () => {
      // Use 550px as the breakpoint where 7 days can comfortably fit
      const canShow7Days = window.innerWidth >= 550;
      setDaysToShow(canShow7Days ? 7 : 3);
    };

    // Set initial value
    handleResize();

    // Add event listener for responsive behavior
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const startOfCurrentPeriod = currentDate; // Start from the current date
  const endOfCurrentPeriod = addDays(startOfCurrentPeriod, daysToShow - 1); // Calculate the end date
  const { appointmentAvailabilities, isLoading } = useAppointmentAvailabilities({
    startDate: format(startOfCurrentPeriod, 'yyyy-MM-dd'),
    endDate: format(endOfCurrentPeriod, 'yyyy-MM-dd'),
    serviceId,
    operatorIds,
    language: 'en',
  });
  const daysToRender = Array.from({ length: daysToShow }, (_, i) => addDays(startOfCurrentPeriod, i));

  const availabilitesByDate = useMemo(() => {
    const map = new Map<string, AppointmentSlot[]>();
    appointmentAvailabilities?.forEach((availability) => {
      const date = availability.date;
      map.set(date, availability.availabilities);
    });
    return map;
  }, [appointmentAvailabilities]);

  const handlePreviousPeriod = () => {
    setCurrentDate((prev) => addDays(prev, -daysToShow));
  };

  const handleNextPeriod = () => {
    setCurrentDate((prev) => addDays(prev, daysToShow));
  };

  // Returns availability info including color, status text, and slot count
  const getAvailabilityInfo = (day: Date) => {
    const dateKey = format(day, 'yyyy-MM-dd');
    const slots = availabilitesByDate.get(dateKey);
    const count = slots?.length || 0;

    if (!slots || count === 0) {
      return {
        colorClass: 'bg-gray-300',
        statusText: t('booking.availability.none'),
        ariaStatus: t('booking.availability.noAppointments'),
        count: 0
      };
    }
    if (count > 5) {
      return {
        colorClass: 'bg-green-100',
        statusText: t('booking.availability.many'),
        ariaStatus: t('booking.availability.manyAvailable', { count }),
        count
      };
    }
    return {
      colorClass: 'bg-yellow-100',
      statusText: t('booking.availability.few'),
      ariaStatus: t('booking.availability.fewAvailable', { count }),
      count
    };
  };

  const canGoBack = useMemo(() => {
    const previousPeriodStart = subDays(currentDate, daysToShow);
    const previousPeriodEnd = subDays(currentDate, 1);
    const tomorrow = addDays(new Date(), 1);
    
    // Allow going back if any day in the previous period would be valid (>= tomorrow)
    return !isBefore(previousPeriodEnd, tomorrow);
  }, [currentDate, daysToShow]);

  const canGoForward = useMemo(() => {
    if (!endDate) return true;
    
    const nextPeriodStart = addDays(currentDate, daysToShow);
    const nextPeriodEnd = addDays(nextPeriodStart, daysToShow - 1);
    const end = new Date(endDate);
    
    // Allow going forward if any day in the next period would be valid (<= endDate)
    return !isBefore(end, nextPeriodStart);
  }, [currentDate, daysToShow, endDate]);

  const isDateDisabled = (day: Date) => {
    const tomorrow = startOfDay(addDays(new Date(), 1));
    const dayStart = startOfDay(day);
    
    // Disable if before tomorrow
    if (isBefore(dayStart, tomorrow)) return true;
    
    // Disable if before startDate
    if (startDate && isBefore(dayStart, startOfDay(new Date(startDate)))) return true;
    
    // Disable if after endDate (endDate should be inclusive/selectable)
    if (endDate && isBefore(startOfDay(new Date(endDate)), dayStart)) return true;
    
    return false;
  };

  return (
    <div role="region" aria-label={t('booking.appointmentPicker')}>
      {!selectedDate && <p className="mt-2" id="date-picker-instruction">{t('booking.selectDay')}</p>}
      <h3 className="text-lg font-medium mb-2" aria-live="polite">
        {format(startOfCurrentPeriod, 'MMMM d', { locale: dateFnsLocales[i18n.language] })} - {format(endOfCurrentPeriod, 'MMMM d', { locale: dateFnsLocales[i18n.language] })}
      </h3>
      <nav className="flex justify-between items-center" aria-label={t('booking.dateNavigation')}>
        <Button
          variant="outline"
          size="icon"
          disabled={!canGoBack}
          aria-label={t('booking.previous')}
          onClick={handlePreviousPeriod}
          className="h-20 min-w-6"
        >
          <ChevronLeft className="w-4 h-4" aria-hidden="true" />
        </Button>
        
        <div
          className={cn(
            "flex space-x-1 flex-1 justify-center",
            daysToShow === 3 ? "px-2" : ""
          )}
          role="group"
          aria-label={t('booking.dateSelector')}
        >
          {daysToRender.map((day, index) => {
            const availability = getAvailabilityInfo(day);
            const isSelected = selectedDate && isSameDay(day, selectedDate);
            const isDisabled = isLoading || isDateDisabled(day);

            return (
              <Button
                key={day.toISOString()}
                variant={isSelected ? "default" : "outline"}
                className={cn(
                  "h-20 flex flex-col",
                  daysToShow === 3 ? "px-1 min-w-0 flex-1" : "px-2"
                )}
                onClick={() => setSelectedDate(day)}
                disabled={isDisabled}
                ref={index === 0 ? firstEnabledDateRef : null}
                aria-label={`${format(day, 'EEEE, MMMM d, yyyy', { locale: dateFnsLocales[i18n.language] })}${isSelected ? `, ${t('booking.selected')}` : ''}, ${availability.ariaStatus}`}
                aria-pressed={isSelected || undefined}
              >
                <div className="flex flex-col items-center space-y-1">
                  <span className={cn(
                    "text-sm",
                    daysToShow === 4 ? "text-xs" : ""
                  )}>{format(day, 'EEE', { locale: dateFnsLocales[i18n.language] })}</span>
                  {isLoading ? (
                    <Skeleton className="w-9 h-9 rounded-full" />
                  ) : (
                    <div className={cn(
                      'p-2 rounded-full text-black relative',
                      daysToShow === 3 ? 'min-w-7 text-sm' : 'min-w-9',
                      availability.colorClass
                    )}>
                      {format(day, 'd')}
                      {/* Visual indicator for screen reader users who may have some vision */}
                      {availability.count === 0 && (
                        <span className="absolute -top-0.5 -right-0.5 text-[8px] text-gray-500" aria-hidden="true">—</span>
                      )}
                    </div>
                  )}
                </div>
              </Button>
            );
          })}
        </div>
        
        <Button
          variant="outline"
          size="icon"
          disabled={!canGoForward}
          aria-label={t('booking.next')}
          onClick={handleNextPeriod}
          className="h-20 min-w-6"
        >
          <ChevronRight className="w-4 h-4" aria-hidden="true" />
        </Button>
      </nav>

      {selectedDate &&
        <AppointmentSlots
          slots={availabilitesByDate.get(format(selectedDate, 'yyyy-MM-dd'))}
          onSelect={onChange}
        />
      }
        
    </div>
  );
};

export default AppointmentPicker;
